import { useState, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ThumbsUp, ThumbsDown, Minus, Search, Sparkles, Loader2, Trash2, Paperclip,
  UserCircle2, X, FileText, Image as ImageIcon, FileSpreadsheet, Upload, Send, ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  listEmployees, listPerformanceEntries, createPerformanceEntry,
  deletePerformanceEntry, generatePerformanceSummary, performanceAttachmentUrl,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// Indian fiscal year (Apr–Mar). Returns "YYYY-YY" (e.g. "2025-26").
const currentFiscalYear = () => {
  const d = new Date();
  const y = d.getFullYear();
  const startYear = d.getMonth() >= 3 ? y : y - 1;
  const endShort = String(startYear + 1).slice(-2);
  return `${startYear}-${endShort}`;
};

const buildFyOptions = () => {
  const now = new Date();
  const startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const options = [];
  for (let i = 0; i < 4; i += 1) {
    const sy = startYear - i;
    options.push(`${sy}-${String(sy + 1).slice(-2)}`);
  }
  return options;
};

const SENTIMENT_META = {
  positive: { label: "Positive", icon: ThumbsUp, cls: "text-green-700 border-green-300 bg-green-50", pillCls: "bg-green-100 text-green-800 border-green-200" },
  negative: { label: "Negative", icon: ThumbsDown, cls: "text-red-700 border-red-300 bg-red-50", pillCls: "bg-red-100 text-red-800 border-red-200" },
  neutral:  { label: "Neutral",  icon: Minus, cls: "text-slate-700 border-slate-300 bg-slate-50", pillCls: "bg-slate-100 text-slate-700 border-slate-200" },
};

const fileIconFor = (mime = "", filename = "") => {
  const m = (mime || "").toLowerCase();
  const n = (filename || "").toLowerCase();
  if (m.startsWith("image/")) return ImageIcon;
  if (m.includes("spreadsheet") || m.includes("excel") || m.includes("csv") || n.endsWith(".xlsx") || n.endsWith(".csv") || n.endsWith(".xls")) return FileSpreadsheet;
  return FileText;
};

// ------------------------------------------------------------------
// Safe markdown renderer — pure JSX, no dangerouslySetInnerHTML (XSS-safe)
// Supports: # / ## headings, - / * / 1. bullets, **bold**, `code`, _em_
// ------------------------------------------------------------------
const INLINE_TOKEN_RE = /(\*\*[^*]+\*\*|`[^`]+`|_[^_]+_)/g;

const renderInline = (text) => {
  if (!text) return null;
  const parts = text.split(INLINE_TOKEN_RE).filter((p) => p !== "");
  return parts.map((part, idx) => {
    if (/^\*\*.+\*\*$/.test(part)) {
      return <strong key={idx}>{part.slice(2, -2)}</strong>;
    }
    if (/^`.+`$/.test(part)) {
      return <code key={idx} className="bg-slate-100 px-1 rounded text-xs">{part.slice(1, -1)}</code>;
    }
    if (/^_.+_$/.test(part)) {
      return <em key={idx}>{part.slice(1, -1)}</em>;
    }
    return <span key={idx}>{part}</span>;
  });
};

const renderMarkdown = (md) => {
  if (!md) return null;
  const lines = md.split("\n");
  const blocks = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="list-disc pl-6 space-y-1 text-sm text-slate-700 mb-3">
          {listBuf.map((l, i) => (
            <li key={`li-${blocks.length}-${i}-${l.slice(0, 20)}`}>{renderInline(l)}</li>
          ))}
        </ul>
      );
      listBuf = [];
    }
  };
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    if (/^##\s+/.test(line)) {
      flushList();
      blocks.push(<h3 key={`h-${i}`} className="font-heading text-sm font-bold text-slate-900 tracking-tight uppercase mt-4 mb-1.5">{line.replace(/^##\s+/, "")}</h3>);
    } else if (/^#\s+/.test(line)) {
      flushList();
      blocks.push(<h2 key={`h-${i}`} className="font-heading text-base font-bold text-slate-900 mt-4 mb-2">{line.replace(/^#\s+/, "")}</h2>);
    } else if (/^\s*[-*]\s+/.test(line)) {
      listBuf.push(line.replace(/^\s*[-*]\s+/, ""));
    } else if (/^\s*\d+\.\s+/.test(line)) {
      listBuf.push(line.replace(/^\s*\d+\.\s+/, ""));
    } else if (line.trim() === "") {
      flushList();
    } else {
      flushList();
      blocks.push(<p key={`p-${i}`} className="text-sm text-slate-700 mb-2 leading-relaxed">{renderInline(line)}</p>);
    }
  });
  flushList();
  return blocks;
};

// ------------------------------------------------------------------
// AddEntryForm
// ------------------------------------------------------------------
const AddEntryForm = ({ employee, onSubmitted }) => {
  const [sentiment, setSentiment] = useState("negative");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  const submitMut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("employee_id", employee.id);
      fd.append("sentiment", sentiment);
      fd.append("title", title);
      fd.append("note", note);
      fd.append("entry_date", entryDate);
      files.forEach((f) => fd.append("files", f));
      return createPerformanceEntry(fd);
    },
    onSuccess: () => {
      toast.success("Entry logged" + (files.length ? " · AI has extracted the attachments" : ""));
      setSentiment("negative");
      setTitle("");
      setNote("");
      setFiles([]);
      setEntryDate(new Date().toISOString().slice(0, 10));
      if (fileInputRef.current) fileInputRef.current.value = "";
      onSubmitted?.();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Save failed"),
  });

  const onFileChange = (e) => {
    const chosen = Array.from(e.target.files || []);
    if (chosen.length > 3) {
      toast.error("Max 3 attachments per entry");
      return;
    }
    setFiles(chosen);
  };

  const canSubmit = !submitMut.isPending && (title.trim() || note.trim() || files.length);

  return (
    <div data-testid="add-entry-form" className="bg-white border border-slate-200 rounded-sm p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Log a moment</p>
          <h3 className="font-heading text-lg font-bold text-slate-900">
            New entry for {employee.name}
          </h3>
        </div>
      </div>

      {/* Sentiment toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold mr-1">Tag</span>
        {Object.entries(SENTIMENT_META).map(([key, meta]) => {
          const Icon = meta.icon;
          const selected = sentiment === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setSentiment(key)}
              data-testid={`sentiment-${key}`}
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border text-xs font-semibold tracking-wide uppercase transition-colors ${
                selected ? meta.cls + " ring-2 ring-offset-1 ring-slate-400" : "border-slate-200 text-slate-500 hover:border-slate-300"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {meta.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Short title</label>
          <Input data-testid="entry-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Late to shift · Missed handover · Great save on defective coil" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Incident date</label>
          <Input data-testid="entry-date" type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} className="font-mono" />
        </div>
      </div>

      <div>
        <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Your thoughts (optional)</label>
        <Textarea
          data-testid="entry-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
          placeholder="Type freely. What happened? Any context? Who else was involved?"
          className="text-sm"
        />
      </div>

      <div className="border-t border-slate-100 pt-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> Or attach evidence — AI will extract the key points
            </p>
            <p className="text-[11px] text-slate-500">PDF · Photo of scribble/letter · Excel/CSV of attendance. Up to 3 files, 8 MB each.</p>
          </div>
          <label
            htmlFor="perf-file-input"
            data-testid="entry-attach-btn"
            className="inline-flex items-center gap-1.5 h-9 px-3 border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 rounded-sm text-xs font-semibold text-slate-700 cursor-pointer"
          >
            <Upload className="h-3.5 w-3.5" /> {files.length ? `${files.length} file(s) chosen` : "Choose files"}
          </label>
          <input
            id="perf-file-input"
            ref={fileInputRef}
            type="file"
            data-testid="entry-file-input"
            multiple
            accept="application/pdf,image/*,.xlsx,.xls,.csv,text/csv"
            className="hidden"
            onChange={onFileChange}
          />
        </div>
        {files.length > 0 && (
          <ul className="mt-2 space-y-1">
            {files.map((f) => {
              const Icon = fileIconFor(f.type, f.name);
              return (
                <li key={`${f.name}-${f.size}-${f.lastModified || 0}`} className="flex items-center gap-2 text-xs text-slate-600 font-mono">
                  <Icon className="h-3.5 w-3.5 text-slate-400" />
                  <span className="truncate flex-1">{f.name}</span>
                  <span className="text-slate-400">{(f.size / 1024).toFixed(1)} KB</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button
          onClick={() => submitMut.mutate()}
          disabled={!canSubmit}
          data-testid="entry-submit"
          className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-10"
        >
          {submitMut.isPending ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving{files.length ? " · Gemini extracting…" : "…"}</>
          ) : (
            <><Send className="h-4 w-4 mr-2" /> Save entry</>
          )}
        </Button>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// EntryCard
// ------------------------------------------------------------------
const EntryCard = ({ entry, onDelete }) => {
  const [showExtract, setShowExtract] = useState(false);
  const meta = SENTIMENT_META[entry.sentiment] || SENTIMENT_META.neutral;
  const Icon = meta.icon;
  return (
    <div data-testid={`entry-card-${entry.id}`} className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-semibold px-1.5 py-0.5 rounded-sm border ${meta.pillCls}`}>
              <Icon className="h-3 w-3" /> {meta.label}
            </span>
            <span className="text-[11px] text-slate-500 font-mono">{(entry.entry_date || "").slice(0, 10)}</span>
            {entry.attachments?.length > 0 && (
              <span className="text-[11px] text-slate-500 flex items-center gap-1">
                <Paperclip className="h-3 w-3" /> {entry.attachments.length}
              </span>
            )}
          </div>
          {entry.title && <h4 className="font-semibold text-slate-900 mt-1">{entry.title}</h4>}
        </div>
        <button
          onClick={() => onDelete(entry)}
          data-testid={`entry-delete-${entry.id}`}
          className="text-slate-400 hover:text-red-600 p-1"
          aria-label="Delete entry"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      {entry.note && (
        <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{entry.note}</p>
      )}
      {entry.attachments?.length > 0 && (
        <div className="space-y-1.5 border-t border-slate-100 pt-2">
          {entry.attachments.map((a) => {
            const FIcon = fileIconFor(a.mime_type, a.filename);
            return (
              <div key={a.id} className="text-xs">
                <div className="flex items-center gap-2">
                  <FIcon className="h-3.5 w-3.5 text-slate-400" />
                  <a
                    href={performanceAttachmentUrl(entry.id, a.id)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`attachment-link-${a.id}`}
                    className="text-blue-700 hover:text-blue-900 font-mono truncate"
                  >
                    {a.filename}
                  </a>
                  <span className="text-slate-400">{(a.size_bytes / 1024).toFixed(1)} KB</span>
                  {a.extracted_text && (
                    <button
                      onClick={() => setShowExtract((s) => !s)}
                      data-testid={`toggle-extract-${a.id}`}
                      className="ml-auto text-[10px] tracking-wider uppercase text-slate-500 hover:text-slate-900 font-semibold"
                    >
                      {showExtract ? "Hide" : "Show"} AI extract
                    </button>
                  )}
                </div>
                {showExtract && a.extracted_text && (
                  <pre className="mt-1 bg-slate-50 border border-slate-200 rounded-sm p-2 text-[11px] text-slate-700 whitespace-pre-wrap font-body">{a.extracted_text}</pre>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="text-[10px] text-slate-400 uppercase tracking-wider">
        Logged by {entry.created_by_name || "—"} · {(entry.created_at || "").slice(0, 16).replace("T", " ")}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// SummaryModal
// ------------------------------------------------------------------
const SummaryModal = ({ result, onClose }) => (
  <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
    <div data-testid="summary-modal" className="bg-white rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-xl">
      <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">AI Summary · FY {result.fiscal_year}</p>
          <h3 className="font-heading text-lg font-bold text-slate-900">{result.employee_name}</h3>
        </div>
        <button onClick={onClose} data-testid="summary-close" className="text-slate-400 hover:text-slate-900">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="px-5 py-4">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-semibold px-2 py-1 rounded-sm border bg-green-50 text-green-800 border-green-200">
            <ThumbsUp className="h-3 w-3" /> {result.counts?.positive || 0} positive
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-semibold px-2 py-1 rounded-sm border bg-red-50 text-red-800 border-red-200">
            <ThumbsDown className="h-3 w-3" /> {result.counts?.negative || 0} negative
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-semibold px-2 py-1 rounded-sm border bg-slate-50 text-slate-700 border-slate-200">
            <Minus className="h-3 w-3" /> {result.counts?.neutral || 0} neutral
          </span>
          <span className="text-[10px] tracking-wider uppercase font-semibold px-2 py-1 rounded-sm border bg-slate-900 text-white border-slate-900">
            {result.entry_count} total
          </span>
        </div>
        <div className="prose prose-sm max-w-none" data-testid="summary-markdown">
          {renderMarkdown(result.summary_markdown)}
        </div>
      </div>
    </div>
  </div>
);

// ------------------------------------------------------------------
// Main page
// ------------------------------------------------------------------
const Performance = () => {
  const qc = useQueryClient();
  const [selectedEmpId, setSelectedEmpId] = useState(null);
  const [search, setSearch] = useState("");
  const [fy, setFy] = useState(currentFiscalYear());
  const [summary, setSummary] = useState(null);
  const fyOptions = useMemo(() => buildFyOptions(), []);

  const { data: employees = [], isLoading: empLoading } = useQuery({
    queryKey: ["masters", "employee"],
    queryFn: listEmployees,
  });

  const activeEmployees = useMemo(
    () => (employees || []).filter((e) => e.active !== false && e.status === "active"),
    [employees]
  );

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return activeEmployees;
    return activeEmployees.filter((e) => {
      const meta = e.meta || {};
      return [e.name, e.code, meta.department, meta.skill_grade, meta.designation, meta.phone, meta.email]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [activeEmployees, search]);

  const selected = useMemo(
    () => (selectedEmpId ? employees.find((e) => e.id === selectedEmpId) : null),
    [employees, selectedEmpId]
  );

  const { data: entries = [], isLoading: entriesLoading } = useQuery({
    queryKey: ["performance-entries", selectedEmpId, fy],
    queryFn: () => listPerformanceEntries(selectedEmpId, fy),
    enabled: Boolean(selectedEmpId),
  });

  const invalidateEntries = () => qc.invalidateQueries({ queryKey: ["performance-entries", selectedEmpId] });

  const deleteMut = useMutation({
    mutationFn: (entryId) => deletePerformanceEntry(entryId),
    onSuccess: () => { toast.success("Entry deleted"); invalidateEntries(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const summariseMut = useMutation({
    mutationFn: () => generatePerformanceSummary(selectedEmpId, fy),
    onSuccess: (data) => setSummary(data),
    onError: (e) => toast.error(e?.response?.data?.detail || "Summary failed"),
  });

  const handleDelete = (entry) => {
    if (window.confirm(`Delete this ${entry.sentiment} entry from ${(entry.entry_date || "").slice(0, 10)}?`)) {
      deleteMut.mutate(entry.id);
    }
  };

  return (
    <div data-testid="performance-page" className="space-y-6">
      <div className="border-b border-slate-200 pb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold mb-1">People</p>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Performance Journal</h2>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Log positive &amp; negative moments as they happen — free-form notes, complaint letters, scribbled feedback,
            attendance sheets. Ask AI to summarise a fiscal year in one click.
          </p>
        </div>
        <Link
          to="/masters"
          data-testid="link-to-masters"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 border border-slate-300 bg-slate-50 hover:bg-slate-100 rounded-sm px-3 py-2"
        >
          Manage employees in Masters <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5">
        {/* -------- Employee list -------- */}
        <aside className="bg-white border border-slate-200 rounded-sm p-3 lg:sticky lg:top-20 lg:self-start lg:max-h-[80vh] lg:overflow-y-auto">
          <div className="relative mb-2">
            <Search className="h-4 w-4 text-slate-400 absolute left-2 top-2.5" />
            <input
              data-testid="employee-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, dept…"
              className="w-full h-9 pl-8 pr-2 border border-slate-200 rounded-sm text-sm"
            />
          </div>
          {empLoading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : filteredEmployees.length === 0 ? (
            <div className="py-8 text-center">
              <UserCircle2 className="h-8 w-8 text-slate-300 mx-auto mb-1.5" />
              <p className="text-xs text-slate-500 mb-2">
                {activeEmployees.length === 0 ? "No employees yet." : "No match."}
              </p>
              {activeEmployees.length === 0 && (
                <Link to="/masters" className="text-xs text-blue-700 hover:underline font-semibold">
                  Add employees in Masters →
                </Link>
              )}
            </div>
          ) : (
            <ul className="space-y-0.5">
              {filteredEmployees.map((emp) => (
                <li key={emp.id}>
                  <button
                    onClick={() => { setSelectedEmpId(emp.id); setSummary(null); }}
                    data-testid={`employee-item-${emp.id}`}
                    className={`w-full text-left px-2 py-2 rounded-sm border-l-2 transition-colors ${
                      selectedEmpId === emp.id ? "border-slate-900 bg-slate-100" : "border-transparent hover:bg-slate-50"
                    }`}
                  >
                    <p className="text-sm font-semibold text-slate-900 truncate">{emp.name}</p>
                    <p className="text-[11px] text-slate-500 truncate font-mono">
                      {emp.code || "—"} · {emp.meta?.department || emp.meta?.skill_grade || emp.meta?.designation || "—"}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* -------- Right pane -------- */}
        <section className="min-w-0 space-y-5">
          {!selected ? (
            <div data-testid="no-employee-selected" className="bg-white border border-dashed border-slate-300 rounded-sm p-10 text-center">
              <UserCircle2 className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-600 mb-1">Select an employee from the left to start logging.</p>
              {activeEmployees.length === 0 && (
                <Link to="/masters" className="text-xs text-blue-700 hover:underline font-semibold">Add your first employee in Masters →</Link>
              )}
            </div>
          ) : (
            <>
              {/* Selected employee header */}
              <div data-testid="selected-employee-header" className="bg-white border border-slate-200 rounded-sm p-5 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Employee</p>
                  <h3 className="font-heading text-xl font-bold text-slate-900">{selected.name}</h3>
                  <p className="text-xs text-slate-500 font-mono mt-0.5">
                    {selected.code || "—"} · {selected.meta?.department || "—"} · {selected.meta?.skill_grade || selected.meta?.designation || "—"}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold mb-0.5">Fiscal year</p>
                    <select
                      data-testid="fy-select"
                      value={fy}
                      onChange={(e) => setFy(e.target.value)}
                      className="h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono bg-white"
                    >
                      {fyOptions.map((opt) => (
                        <option key={opt} value={opt}>FY {opt}</option>
                      ))}
                    </select>
                  </div>
                  <Button
                    onClick={() => summariseMut.mutate()}
                    disabled={summariseMut.isPending}
                    data-testid="summarise-btn"
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm h-10 mt-4 sm:mt-5"
                  >
                    {summariseMut.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Summarising…</> : <><Sparkles className="h-4 w-4 mr-2" /> Summarize with AI</>}
                  </Button>
                </div>
              </div>

              <AddEntryForm employee={selected} onSubmitted={invalidateEntries} />

              {/* Entries timeline */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">
                    {entries.length} {entries.length === 1 ? "entry" : "entries"} · FY {fy}
                  </p>
                </div>
                {entriesLoading ? (
                  <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
                ) : entries.length === 0 ? (
                  <div data-testid="empty-entries" className="bg-white border border-dashed border-slate-300 rounded-sm p-8 text-center">
                    <p className="text-sm text-slate-500">Nothing logged for {selected.name} in FY {fy} yet.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {entries.map((entry) => (
                      <EntryCard key={entry.id} entry={entry} onDelete={handleDelete} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </div>

      {summary && <SummaryModal result={summary} onClose={() => setSummary(null)} />}
    </div>
  );
};

export default Performance;
